import { useRef, useState } from 'react';
import { Button, Select, Space, Tag, Tooltip } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

import type { FileType } from '@/types';

interface SortableFileTypeSelectProps {
  value?: number[];
  onChange?: (ids: number[]) => void;
  allFileTypes: FileType[];
  disabled?: boolean;
}

interface FileTypeOption {
  value: number;
  label: string;
  code: string;
  description?: string;
  disabled: boolean;
}

const SortableFileTypeSelect = ({ value = [], onChange, allFileTypes, disabled }: SortableFileTypeSelectProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const skipNextClose = useRef(false);

  const selectedTypes = value
    .map((id) => allFileTypes.find((ft) => ft.id === id))
    .filter((ft): ft is FileType => ft !== undefined);

  const options: FileTypeOption[] = allFileTypes.map((ft) => ({
    value: ft.id,
    label: ft.name,
    code: ft.code,
    description: ft.description,
    disabled: value.includes(ft.id),
  }));

  const filterOption = (input: string, option?: FileTypeOption) => {
    const keyword = input.trim().toLowerCase();
    if (!keyword) return true;
    return [option?.label, option?.code, option?.description].some((field) =>
      (field || '').toLowerCase().includes(keyword),
    );
  };

  const handleAdd = (id: number) => {
    onChange?.([...value, id]);
    skipNextClose.current = true;
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open && skipNextClose.current) {
      skipNextClose.current = false;
      return;
    }
    setDropdownOpen(open);
  };

  const handleRemove = (id: number) => {
    onChange?.(value.filter((v) => v !== id));
  };

  const handleMoveUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...value];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange?.(next);
  };

  const handleMoveDown = (idx: number) => {
    if (idx === value.length - 1) return;
    const next = [...value];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange?.(next);
  };

  return (
    <div>
      <Select
        placeholder="搜索并添加文件类型"
        options={options}
        value={undefined}
        onChange={handleAdd}
        open={dropdownOpen}
        onOpenChange={handleDropdownVisibleChange}
        style={{ width: '100%', marginBottom: 8 }}
        allowClear={false}
        disabled={disabled}
        showSearch={{ filterOption }}
        optionRender={(option) => {
          const selected = value.includes(option.data.value);
          return (
            <span style={{ color: selected ? '#bbb' : undefined }}>
              {selected && <CheckOutlined style={{ marginRight: 6 }} />}
              {option.label}
              {option.data?.description && (
                <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>{option.data.description}</span>
              )}
            </span>
          );
        }}
      />
      <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: '4px 0', minHeight: 38 }}>
        {selectedTypes.length === 0 && (
          <div style={{ color: '#bbb', padding: '4px 12px', fontSize: 13 }}>未选择文件类型</div>
        )}
        {selectedTypes.map((ft, idx) => (
          <div
            key={ft.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '2px 8px',
              gap: 6,
            }}
          >
            <Tag style={{ minWidth: 20, textAlign: 'center', margin: 0 }}>{idx + 1}</Tag>
            <Tooltip title={ft.description || undefined}>
              <span style={{ flex: 1, fontSize: 13, cursor: ft.description ? 'help' : undefined }}>{ft.name}</span>
            </Tooltip>
            <Space size={0}>
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={disabled || idx === 0}
                onClick={() => handleMoveUp(idx)}
              />
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={disabled || idx === selectedTypes.length - 1}
                onClick={() => handleMoveDown(idx)}
              />
              <Button
                type="text"
                size="small"
                danger
                icon={<CloseOutlined />}
                disabled={disabled}
                onClick={() => handleRemove(ft.id)}
              />
            </Space>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SortableFileTypeSelect;
